/**
 * CrmActivityOriginFilter - Filtro de origem da atividade
 * 
 * Permite selecionar: Todas Origens, Manual ou Sistema.
 * Segue o padrão visual dos outros filtros CRM (Popover + Command).
 * 
 * @author Sistema
 * @version 1.0.0
 * RELEASE: 2026-10-05 v1.1.0 - Gatilho do filtro segue o modelo de pill: sem a linha cinza da borda, fundo de superfície, sombra sutil e cantos arredondados (rounded-full).
 * RELEASE: 2026-03-03 v1.0.0 - Implementação inicial
 */

import React, { useState } from 'react';
import { Check, ChevronDown, List, User, Bot } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Command,
  CommandGroup,
  CommandItem,
  CommandList,
} from '@/components/ui/command';

/**
 * Tipo para filtro de origem da atividade
 * - 'all': Todas as atividades (sem filtro)
 * - 'manual': Somente atividades criadas manualmente
 * - 'system': Somente atividades geradas pelo sistema
 */
export type ActivityOriginFilter = 'all' | 'manual' | 'system';

/**
 * Props do componente CrmActivityOriginFilter
 * @param value - Origem selecionada (all, manual, system)
 * @param onChange - Callback para mudança de seleção
 * @param className - Classes CSS adicionais
 */
interface CrmActivityOriginFilterProps {
  value: ActivityOriginFilter;
  onChange: (origin: ActivityOriginFilter) => void;
  className?: string;
}

/**
 * Opções de origem disponíveis
 */
const ORIGIN_OPTIONS: { value: ActivityOriginFilter; label: string; icon: typeof List }[] = [
  { value: 'all', label: 'Todas Origens', icon: List },
  { value: 'manual', label: 'Manual', icon: User },
  { value: 'system', label: 'Sistema', icon: Bot },
];

/**
 * Componente de filtro de origem de atividade
 */
export function CrmActivityOriginFilter({
  value,
  onChange,
  className,
}: CrmActivityOriginFilterProps) {
  const [open, setOpen] = useState(false);

  // Opção selecionada
  const selectedOption = ORIGIN_OPTIONS.find(o => o.value === value) || ORIGIN_OPTIONS[0];

  // Handler para seleção
  const handleSelect = (origin: ActivityOriginFilter) => {
    onChange(origin);
    setOpen(false);
  };

  // Detecta se é versão compacta baseado na classe
  const isCompact = className?.includes('h-[22px]');

  // Ícone dinâmico
  const SelectedIcon = selectedOption.icon;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className={cn(
            'h-9 px-3 gap-2 font-normal border-0 bg-background rounded-full',
            className
          )}
        >
          <SelectedIcon className={cn(isCompact ? 'h-3 w-3' : 'h-4 w-4', 'opacity-70')} />
          <span className="hidden sm:inline truncate">{selectedOption.label}</span>
          <ChevronDown className={cn(isCompact ? 'h-2.5 w-2.5' : 'h-3 w-3', 'opacity-50')} />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[180px] p-0" align="start">
        <Command>
          <CommandList>
            <CommandGroup heading="Origem">
              {ORIGIN_OPTIONS.map(option => {
                const OptionIcon = option.icon;
                return (
                  <CommandItem
                    key={option.value}
                    value={option.value}
                    onSelect={() => handleSelect(option.value)}
                    className="cursor-pointer"
                  >
                    <div
                      className={cn(
                        "mr-2 h-4 w-4 rounded-full border flex items-center justify-center",
                        value === option.value
                          ? "border-primary bg-primary"
                          : "border-muted"
                      )}
                    >
                      {value === option.value && (
                        <Check className="h-2.5 w-2.5 text-primary-foreground" />
                      )}
                    </div>
                    <OptionIcon className="h-4 w-4 mr-2 opacity-70" />
                    <span>{option.label}</span>
                  </CommandItem>
                );
              })}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
